import { useState } from 'react'
import { api } from '../../api/client'
import { useUsers } from '../../api/queries'
import { useQueryClient } from '@tanstack/react-query'
import { useAuth } from '../../auth/AuthProvider'
import { OnboardingCard } from '../../components/OnboardingCard'
import { Avatar, Card, EmptyState, ErrorBox, Loading } from '../../components/ui'
import { formatDateTime } from '../../lib/format'
import { PhoneLink } from '../../components/SiteContact'
import { Roles, type DeleteUsersResponse, type HideUsersResponse, type User } from '../../api/types'

// Kontor og Lærling (beslutning 108). En rolle giver kun det, den giver - en lærling, der også er
// medarbejder, får VPN som medarbejder. Fjern "Medarbejder", når en konto gøres til lærling.
const allRoles = [Roles.Superadmin, Roles.Administrator, Roles.Office, Roles.ProjectManager, Roles.Employee, Roles.Apprentice]

/**
 * Brugeradministration. Microsoft 365-brugere oprettes automatisk ved første login;
 * lokale brugere (uden 365-konto) oprettes her med en adgangskode.
 */
export function UsersPage() {
  const queryClient = useQueryClient()
  const { hasRole } = useAuth()
  const [showHidden, setShowHidden] = useState(false)
  const { data, isLoading, error } = useUsers({ includeHidden: showHidden })
  const [showForm, setShowForm] = useState(false)
  const [selected, setSelected] = useState<number[]>([])
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState<string | null>(null)

  const hiddenCount = data?.filter((u) => u.isHidden).length ?? 0
  const isSuperadmin = hasRole('Superadmin')

  function toggle(id: number) {
    setSelected((current) => (current.includes(id) ? current.filter((x) => x !== id) : [...current, id]))
  }

  async function setHidden(isHidden: boolean) {
    setBusy(true)
    setMessage(null)
    try {
      const result = await api.put<HideUsersResponse>('/api/users/hidden', { userIds: selected, isHidden })
      setSelected([])
      await queryClient.invalidateQueries({ queryKey: ['users'] })

      const verb = isHidden ? 'skjult' : 'sat ind igen'
      setMessage(
        result.skipped.length > 0
          ? `${result.changed} ${verb}. Sprunget over: ${result.skipped.join(', ')}.`
          : `${result.changed} bruger${result.changed === 1 ? '' : 'e'} ${verb}.`
      )
    } catch (err) {
      setMessage(err instanceof Error ? err.message : 'Handlingen mislykkedes.')
    } finally {
      setBusy(false)
    }
  }

  /**
   * Sletning kan ikke fortrydes, og den lykkes kun for brugere uden spor
   * (beslutning 039). Svaret siger, hvem der blev sprunget over og hvorfor.
   */
  async function deleteSelected() {
    const navne = data?.filter((u) => selected.includes(u.id)).map((u) => u.displayName) ?? []

    if (
      !window.confirm(
        `Slet ${selected.length} bruger${selected.length === 1 ? '' : 'e'} permanent?

` +
          `${navne.slice(0, 8).join(', ')}${navne.length > 8 ? ' m.fl.' : ''}

` +
          'Brugere der har lavet noget i systemet bliver sprunget over. Handlingen kan ikke fortrydes.'
      )
    ) {
      return
    }

    setBusy(true)
    setMessage(null)
    try {
      const result = await api.post<DeleteUsersResponse>('/api/users/delete', { userIds: selected })
      setSelected([])
      await queryClient.invalidateQueries({ queryKey: ['users'] })

      const dele = [`${result.deleted} slettet`]
      if (result.skipped.length > 0) {
        dele.push(
          'ikke slettet: ' + result.skipped.map((u) => `${u.displayName} (${u.reason})`).join('; ')
        )
      }
      setMessage(dele.join('. ') + '.')
    } catch (err) {
      setMessage(err instanceof Error ? err.message : 'Sletningen mislykkedes.')
    } finally {
      setBusy(false)
    }
  }

  if (isLoading) return <Loading />
  if (error) return <ErrorBox error={error} />

  return (
    <div className="stack" style={{ gap: 16 }}>
      <div className="row row--between">
        <p className="small muted" style={{ margin: 0 }}>
          Microsoft 365-brugere oprettes automatisk ved første login. Lokale brugere oprettes her.
        </p>
        <button className="btn btn--primary" onClick={() => setShowForm((v) => !v)}>
          {showForm ? 'Luk' : 'Opret lokal bruger'}
        </button>
      </div>

      {showForm && <NewLocalUserForm onDone={() => setShowForm(false)} />}

      {/* Hvem mangler at komme ind i systemet (beslutning 080). Står øverst, fordi
          det er det, der skal handles på - listen nedenfor er opslagsværket. */}
      <OnboardingCard />

      {!data || data.length === 0 ? (
        <EmptyState>Ingen brugere endnu.</EmptyState>
      ) : (
        <Card title={`Brugere (${data.length})`}>
          <p className="tiny muted" style={{ marginTop: 0 }}>
            Microsoft 365 henter også postkasser, mødelokaler og servicekonti med. Marker dem her og
            <strong> skjul</strong> dem - så forsvinder de fra kalenderen, tildelingslisterne og
            søgningen, og synkroniseringen rører dem ikke igen.
            {isSuperadmin && (
              <>
                {' '}
                <strong>Slet</strong> fjerner dem helt, men virker kun for konti, der aldrig har lavet
                noget. Har brugeren én opgave, kommentar eller dag i kalenderen, bliver vedkommende
                sprunget over - historikken må ikke forsvinde.
              </>
            )}
          </p>

          <div className="row row--between" style={{ marginBottom: 10, flexWrap: 'wrap', gap: 8 }}>
            <label className="row tiny" style={{ gap: 6 }}>
              <input type="checkbox" checked={showHidden} onChange={(e) => setShowHidden(e.target.checked)} />
              Vis skjulte{hiddenCount > 0 && showHidden ? ` (${hiddenCount})` : ''}
            </label>

            {selected.length > 0 && (
              <div className="btn-group">
                <button className="btn btn--sm" disabled={busy} onClick={() => void setHidden(true)}>
                  Skjul {selected.length} valgte
                </button>
                {showHidden && (
                  <button className="btn btn--sm" disabled={busy} onClick={() => void setHidden(false)}>
                    Sæt ind igen
                  </button>
                )}
                {isSuperadmin && (
                  <button className="btn btn--danger btn--sm" disabled={busy} onClick={() => void deleteSelected()}>
                    Slet
                  </button>
                )}
                <button className="btn btn--sm" onClick={() => setSelected([])}>
                  Ryd valg
                </button>
              </div>
            )}
          </div>

          {message && (
            <div className="small" style={{ marginBottom: 10 }}>
              {message}
            </div>
          )}

          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th style={{ width: 32 }}></th>
                  <th>Navn</th>
                  <th>E-mail</th>
                  <th>Login</th>
                  <th>Roller</th>
                  <th>Seneste login</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {data.map((user) => (
                  <UserRow
                    key={user.id}
                    user={user}
                    selected={selected.includes(user.id)}
                    onToggle={() => toggle(user.id)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  )
}

function NewLocalUserForm({ onDone }: { onDone: () => void }) {
  const queryClient = useQueryClient()
  const { isAdmin, isSuperadmin } = useAuth()
  // Kun en Superadmin kan tildele rollen Superadmin (UsersController.CreateLocalUser).
  const roleChoices = isSuperadmin ? allRoles : allRoles.filter((r) => r !== Roles.Superadmin)
  const [displayName, setDisplayName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [role, setRole] = useState<string>(Roles.Employee)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  async function submit(event: React.FormEvent) {
    event.preventDefault()
    setError(null)
    setBusy(true)
    try {
      await api.post('/api/users', { displayName, email, password, roleNames: [role], mustChangePassword: true })
      await queryClient.invalidateQueries({ queryKey: ['users'] })
      onDone()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Kunne ikke oprette brugeren.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card title="Ny lokal bruger (uden Microsoft 365)">
      <form onSubmit={submit}>
        <div className="form-grid">
          <label className="field">
            <span className="field__label">Navn *</span>
            <input required value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
          </label>
          <label className="field">
            <span className="field__label">E-mail *</span>
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
          </label>
          <label className="field">
            <span className="field__label">Midlertidig adgangskode * (min. 8 tegn, bogstaver og tal)</span>
            <input type="text" required autoComplete="off" value={password} onChange={(e) => setPassword(e.target.value)} />
          </label>
          <label className="field">
            <span className="field__label">Rolle</span>
            <select value={role} onChange={(e) => setRole(e.target.value)}>
              {roleChoices.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          </label>
        </div>
        <p className="tiny muted">
          Brugeren bliver bedt om at skifte adgangskoden ved første login. Rollerne kan ændres bagefter i listen.
        </p>
        {error && <div className="error-box" style={{ marginBottom: 10 }}>{error}</div>}
        <div className="btn-group">
          <button className="btn btn--primary" type="submit" disabled={busy || !isAdmin}>
            Opret bruger
          </button>
          <button className="btn" type="button" onClick={onDone}>
            Fortryd
          </button>
        </div>
      </form>
    </Card>
  )
}

function UserRow({
  user,
  selected,
  onToggle
}: {
  user: User
  selected: boolean
  onToggle: () => void
}) {
  const queryClient = useQueryClient()
  const { hasRole } = useAuth()
  const isSuperadmin = hasRole('Superadmin')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [info, setInfo] = useState<string | null>(null)
  const [resetPassword, setResetPassword] = useState<string | null>(null)

  async function run(action: () => Promise<unknown>, successText?: string) {
    setBusy(true)
    setError(null)
    setInfo(null)
    try {
      await action()
      await queryClient.invalidateQueries({ queryKey: ['users'] })
      if (successText) setInfo(successText)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Handlingen mislykkedes.')
    } finally {
      setBusy(false)
    }
  }

  function toggleRole(role: string) {
    const next = user.roles.includes(role) ? user.roles.filter((r) => r !== role) : [...user.roles, role]
    void run(() => api.put(`/api/users/${user.id}/roles`, { roleNames: next }))
  }

  // Samme regel som UsersController.SetRoles: kun en Superadmin kan tildele eller fjerne
  // rollen Superadmin. Serveren afviser også ALLE rolleændringer på en Superadmin fra
  // andre, fordi rollelisten, der sendes, stadig indeholder Superadmin.
  const targetIsSuperadmin = user.roles.includes(Roles.Superadmin)
  function roleLocked(role: string) {
    return !isSuperadmin && (role === Roles.Superadmin || targetIsSuperadmin)
  }

  return (
    <tr style={user.isHidden ? { opacity: 0.55 } : undefined}>
      <td>
        <input type="checkbox" checked={selected} onChange={onToggle} aria-label={`Vælg ${user.displayName}`} />
      </td>
      <td>
        <div className="row">
          <Avatar user={user} />
          {user.displayName}
        </div>
        {user.isHidden && <div className="tiny muted">Skjult - vises ikke i kalender og lister</div>}
      </td>
      <td className="small muted">
        {user.email}
        {user.phoneNumber && (
          <div className="tiny">
            <PhoneLink phone={user.phoneNumber} />
          </div>
        )}
      </td>
      <td>
        <span className="pill" style={user.authProvider === 'local' ? { background: '#FEF3C7', color: '#92400E' } : { background: '#DBEAFE', color: '#1E40AF' }}>
          {user.authProvider === 'local' ? 'Lokal' : 'Microsoft 365'}
        </span>
        {user.mustChangePassword && <div className="tiny muted">Skal skifte adgangskode</div>}
        {user.twoFactorEnabled && <div className="tiny muted">To-faktor slået til</div>}
        {user.twoFactorRequired && !user.twoFactorEnabled && (
          <div className="tiny muted">Skal sætte to-faktor op ved næste login</div>
        )}
      </td>
      <td>
        <div className="btn-group">
          {allRoles.map((role) => (
            <button
              key={role}
              className={`btn btn--sm ${user.roles.includes(role) ? 'btn--primary' : ''}`}
              disabled={busy || roleLocked(role)}
              title={roleLocked(role) ? 'Kun en Superadmin kan ændre rollen Superadmin og rollerne på en Superadmin' : undefined}
              onClick={() => toggleRole(role)}
            >
              {role}
            </button>
          ))}
        </div>
        {!isSuperadmin && targetIsSuperadmin && (
          <div className="tiny muted">Kun en Superadmin kan ændre roller på en Superadmin</div>
        )}
        {error && <div className="tiny overdue">{error}</div>}
        {info && <div className="tiny" style={{ color: 'var(--success)' }}>{info}</div>}
      </td>
      <td className="small muted">{formatDateTime(user.lastLoginAt)}</td>
      <td>
        <div className="btn-group">
          <button
            className={`btn btn--sm ${user.isActive ? '' : 'btn--danger'}`}
            disabled={busy}
            onClick={() => void run(() => api.put(`/api/users/${user.id}/active`, { isActive: !user.isActive }))}
          >
            {user.isActive ? 'Aktiv' : 'Deaktiveret'}
          </button>
          {user.authProvider === 'local' && resetPassword === null && (
            <button className="btn btn--sm" disabled={busy} onClick={() => setResetPassword('')}>
              Nulstil adgangskode
            </button>
          )}
          {/*
            Sidste udvej, når både telefonen og genoprettelseskoderne er væk.
            Kun Superadmin - den der kan slå to-faktor fra på en anden konto,
            kan i praksis overtage den (beslutning 035).
          */}
          {isSuperadmin && user.authProvider === 'local' && user.twoFactorEnabled && (
            <button
              className="btn btn--sm"
              disabled={busy}
              onClick={() => {
                if (window.confirm(`Nulstil to-faktor for ${user.displayName}? Brugeren skal sætte det op igen.`)) {
                  void run(() => api.post(`/api/users/${user.id}/reset-two-factor`, {}), 'To-faktor er nulstillet.')
                }
              }}
            >
              Nulstil to-faktor
            </button>
          )}
        </div>
        {resetPassword !== null && (
          <form
            className="row"
            style={{ marginTop: 6 }}
            onSubmit={(event) => {
              event.preventDefault()
              void run(
                () => api.post(`/api/users/${user.id}/reset-password`, { newPassword: resetPassword }),
                'Ny midlertidig adgangskode er sat.'
              ).then(() => setResetPassword(null))
            }}
          >
            <input placeholder="Ny midlertidig adgangskode" value={resetPassword} onChange={(e) => setResetPassword(e.target.value)} />
            <button className="btn btn--sm btn--primary" type="submit" disabled={busy || resetPassword.length < 8}>
              Gem
            </button>
            <button className="btn btn--sm" type="button" onClick={() => setResetPassword(null)}>
              Fortryd
            </button>
          </form>
        )}
      </td>
    </tr>
  )
}
